import { Link } from "react-router-dom";
import { Phone, MapPin, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import SEO from "@/components/SEO";

const FAQPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (question) => {
    setOpenFaq(openFaq === question ? null : question);
  };

  const faqs = [
    {
      question: "How much do replacement windows cost in Lanarkshire?",
      answer: "In Lanarkshire, the typical cost to replace a single standard uPVC window ranges from £300 to £900 fully fitted, with localised market prices sitting about 8% below the UK national average. For a full-house replacement on a standard 3-bedroom home (approx. 8–10 windows), you can expect an average total cost between £4,000 and £8,500 for uPVC double glazing. These are basic guide prices and costs can change depending on style, colour and hardware chosen."
    },
    {
      question: "How much does window installation cost in Motherwell?",
      answer: "The cost depends on the number of windows, size, and style you choose. We offer competitive pricing and free quotes to give you an accurate cost. Visit our windows page for more details on our range."
    },
    {
      question: "How long does it take to install new windows?",
      answer: "Most standard window installations are completed within a day, depending on the size of the job. Check our recent work gallery for examples of completed projects."
    },
    {
      question: "Do you offer double glazing?",
      answer: "Yes, we specialise in high-quality double glazing installations designed to improve energy efficiency and reduce noise. We offer A-rated uPVC windows, bay windows, casement windows and more."
    },
    {
      question: "Are your windows energy efficient?",
      answer: "Absolutely. Our windows are designed to reduce heat loss and help lower your energy bills. All our double glazing is A-rated for maximum energy efficiency."
    },
    {
      question: "Do you cover areas outside Motherwell?",
      answer: "Yes, we cover Carfin, Wishaw, Bellshill, Hamilton, Coatbridge, Airdrie, Glasgow and across Lanarkshire. Contact us for a free quote in your area."
    },
    {
      question: "What types of doors do you install?",
      answer: "We install composite doors, uPVC doors, French doors, and patio doors in a wide range of colours and styles. All our doors come with multi-point locking and a 10 year guarantee."
    },
    {
      question: "Do you build garden rooms?",
      answer: "Yes, we design and build premium garden rooms perfect for home offices, gyms, man caves or guest suites. All our garden rooms are fully insulated with double glazed windows and doors."
    }
  ];

  const services = [
    "uPVC window installation",
    "Double glazing installation",
    "Window replacements",
    "Energy-efficient window upgrades",
    "Window repairs and maintenance"
  ];

  const benefits = [
    "Local and trusted in Motherwell & Lanarkshire",
    "Competitive pricing with free quotes",
    "High-quality materials and workmanship",
    "Fast, clean, and reliable service",
    "Friendly and professional approach"
  ];

  const areas = [
    "Motherwell", "Carfin", "Wishaw", "Bellshill",
    "Hamilton", "Coatbridge", "Airdrie", "Across Lanarkshire"
  ];

  const locationServices = [
    {
      title: "Window Installation in Wishaw",
      description: "We offer expert window installation in Wishaw, providing high-quality uPVC and double glazing solutions for homes and properties throughout the area. Our team delivers efficient, reliable installations designed to improve energy efficiency and security."
    },
    {
      title: "Window Installation in Bellshill",
      description: "Looking for trusted window installers in Bellshill? We supply and fit modern, energy-efficient windows tailored to your property, ensuring long-lasting performance and a clean, professional finish."
    },
    {
      title: "Window Installation in Hamilton",
      description: "Our window installation services in Hamilton include full replacements, upgrades, and double glazing solutions. We help homeowners reduce heat loss and enhance the look of their property."
    },
    {
      title: "Window Installation in Coatbridge",
      description: "We provide reliable window installation in Coatbridge, offering competitive pricing and high-quality workmanship. From single windows to full house upgrades, we've got you covered."
    },
    {
      title: "Window Installation in Airdrie",
      description: "Need window installers in Airdrie? We deliver professional installations using durable, energy-efficient materials that improve comfort and reduce energy bills."
    }
  ];

  return (
    <div data-testid="faq-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Window Installation Motherwell | Double Glazing Hamilton, Glasgow, Lanarkshire | FAQ"
        description="Professional double glazing & uPVC window installation in Motherwell, Hamilton, Glasgow & Lanarkshire. Composite doors, window replacements, glazing repairs. Free quotes - call 07917 181597"
        keywords="window installation Motherwell, double glazing Motherwell, double glazing Hamilton, double glazing Glasgow, window installers Lanarkshire, uPVC windows Motherwell, composite doors Lanarkshire, window replacement Hamilton, double glazing Wishaw, window installation Bellshill, window installation Coatbridge, window installation Airdrie, energy efficient windows Lanarkshire"
        canonical="/faq"
      />

      {/* Hero Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            WINDOW INSTALLATION IN <span className="text-[#FF6B00]">MOTHERWELL</span>
          </h1>
          <p className="text-xl text-[#FF6B00] font-bold mb-4">
            Double Glazing Experts in Lanarkshire
          </p>
          <p className="text-gray-300 max-w-3xl mx-auto">
            Looking for reliable window installation in Motherwell? We specialise in high-quality double glazing and uPVC window installations across Motherwell, Carfin, and the wider Lanarkshire area.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            PROFESSIONAL WINDOW INSTALLATION IN <span className="text-[#FF6B00]">MOTHERWELL</span>
          </h2>
          <p className="text-[#525252] text-lg leading-relaxed">
            With years of experience, we provide durable, energy-efficient windows that improve your home's comfort, security, and appearance. Whether you need <Link to="/windows" className="text-[#FF6B00] hover:underline">A-rated double glazing windows</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link>, or a bespoke <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden room</Link>, our team delivers quality workmanship every time. See what our <Link to="/reviews" className="text-[#FF6B00] hover:underline">customers say about us</Link>.
          </p>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            OUR WINDOW <span className="text-[#FF6B00]">SERVICES</span>
          </h2>
          <p className="text-[#525252] mb-6">
            We offer a full range of services tailored to your needs:
          </p>
          <ul className="space-y-3 mb-8">
            {services.map((service) => (
              <li key={service} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#FF6B00] flex-shrink-0" />
                <span className="text-[#525252]">{service}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#525252]">
            Whether you're upgrading old <Link to="/windows" className="text-[#FF6B00] hover:underline">windows</Link> or fitting out a new property with <Link to="/doors" className="text-[#FF6B00] hover:underline">doors</Link> and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>, we deliver professional results every time. <Link to="/contact" className="text-[#FF6B00] hover:underline">Get a free quote</Link>.
          </p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            WHY CHOOSE <span className="text-[#FF6B00]">US?</span>
          </h2>
          <ul className="space-y-3 mb-8">
            {benefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#FF6B00] flex-shrink-0" />
                <span className="text-[#525252]">{benefit}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#525252]">
            We take pride in every job and ensure customer satisfaction from start to finish. See what our <Link to="/reviews" className="text-[#FF6B00] hover:underline">customers say about us</Link> and browse our <Link to="/recent-work" className="text-[#FF6B00] hover:underline">recent installations</Link>.
          </p>
        </div>
      </section>

      {/* Areas We Cover */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-black text-white mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            AREAS WE <span className="text-[#FF6B00]">COVER</span>
          </h2>
          <p className="text-gray-300 mb-6">
            We provide window installation services in:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {areas.map((area) => (
              <div key={area} className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#FF6B00]" />
                <span className="text-white">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location-Specific Services */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {locationServices.map((service) => (
            <div key={service.title} className="mb-12 last:mb-0">
              <h2 
                className="text-xl sm:text-2xl font-black text-[#0A0A0A] mb-4"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                {service.title.toUpperCase().replace("WINDOW INSTALLATION IN ", "WINDOW INSTALLATION IN ").replace(service.title.split(" ").pop(), "")}
                <span className="text-[#FF6B00]">{service.title.split(" ").pop().toUpperCase()}</span>
              </h2>
              <p className="text-[#525252] leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Energy Efficient Windows */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            ENERGY EFFICIENT <span className="text-[#FF6B00]">WINDOWS</span>
          </h2>
          <p className="text-[#525252] mb-4 leading-relaxed">
            Our modern double glazing solutions help reduce heat loss, lower energy bills, and keep your home warm all year round.
          </p>
          <p className="text-[#525252] leading-relaxed">
            Upgrading your windows is one of the best ways to improve your home's energy efficiency and value.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            FREQUENTLY ASKED <span className="text-[#FF6B00]">QUESTIONS</span>
          </h2>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div 
                key={faq.question} 
                className="border border-[#E5E5E5] bg-white"
              >
                <button
                  onClick={() => toggleFaq(faq.question)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#F5F5F5] transition-colors"
                  data-testid={`faq-question-${faq.question.slice(0, 30).toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="font-bold text-[#0A0A0A] pr-4">{faq.question}</span>
                  {openFaq === faq.question ? (
                    <ChevronUp className="w-5 h-5 text-[#FF6B00] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#FF6B00] flex-shrink-0" />
                  )}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-4">
                    <p className="text-[#525252]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#FF6B00]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-2xl sm:text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            GET A FREE QUOTE TODAY
          </h2>
          <p className="text-white/90 mb-4">
            If you're looking for window installers in Motherwell, we're here to help.
          </p>
          <p className="text-white/90 mb-8">
            We're happy to discuss your requirements and provide expert advice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:07917181597"
              data-testid="faq-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
            >
              <Phone className="w-5 h-5" />
              Call 07917 181597
            </a>
            <Link
              to="/contact"
              data-testid="faq-contact-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all"
            >
              Send a Message
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Explore Our Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/windows" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Double Glazing Windows
            </Link>
            <Link to="/doors" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Composite & uPVC Doors
            </Link>
            <Link to="/outbuilds" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Garden Rooms
            </Link>
            <Link to="/recent-work" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              View Our Recent Work
            </Link>
            <Link to="/reviews" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Customer Reviews
            </Link>
            <Link to="/contact" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Get a Free Quote
            </Link>
          </div>
          <p className="text-xs text-gray-400 mt-6">
            window installation Motherwell, double glazing Motherwell, window installers Lanarkshire, uPVC windows Motherwell, window replacement Motherwell, double glazing Carfin
          </p>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
